import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { User, Lock, Mail, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import { useLang } from "@/context/LanguageContext";
import { validatePersonName, PHONE_PATTERN } from "@/lib/validation";

export default function ClientSignupPage() {
  const { t } = useLang();
  const { signup, user, checking } = useClientAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!checking && user) return <Navigate to="/portal" replace />;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const nameError = validatePersonName(form.name);
    if (nameError) {
      toast.error(nameError);
      return;
    }
    if (form.phone.trim() && !PHONE_PATTERN.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number (digits only).");
      return;
    }
    setSubmitting(true);
    try {
      await signup(form);
      toast.success(t("portal.signupSuccess"));
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.signupError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-gold grid place-items-center px-4 py-12">
      <div className="w-full max-w-md card-dark p-8 sm:p-10 gold-glow" data-testid="client-signup-card">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Eazy-Travels"
            className="h-14 w-auto object-contain mx-auto mb-5 drop-shadow-[0_0_30px_rgba(244,196,48,0.35)]"
          />
          <h1 className="font-display text-2xl font-bold text-white mb-1">{t("portal.signupTitle")}</h1>
          <p className="text-sm text-white/55">{t("portal.signupSubtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("scheduler.fullName")}</label>
            <div className="relative">
              <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input required value={form.name} onChange={upd("name")} className="input-dark pl-10" data-testid="client-signup-name" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input required type="email" value={form.email} onChange={upd("email")} className="input-dark pl-10" data-testid="client-signup-email" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("portal.phoneOptional")}</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input
                type="tel"
                inputMode="tel"
                pattern="[0-9+()\-.\s]{6,30}"
                maxLength={30}
                value={form.phone}
                onChange={upd("phone")}
                className="input-dark pl-10"
                data-testid="client-signup-phone"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("admin.password")}</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input
                required
                minLength={6}
                type="password"
                value={form.password}
                onChange={upd("password")}
                className="input-dark pl-10"
                data-testid="client-signup-password"
              />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="client-signup-submit">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {t("portal.createAccountButton")}
          </button>
        </form>

        <p className="text-center text-xs text-white/50 mt-6">
          {t("portal.alreadyHaveAccount")} <Link to="/portal/login" className="text-[var(--gold)] hover:underline">{t("portal.signIn")}</Link>
        </p>
      </div>
    </div>
  );
}
